import { regularity, type Cadence, type EventDef } from '@decathlon/shared';
import type { ControlEvent } from '../controls.js';
import type { Synth } from '../audio.js';
import type { Athlete } from '../game/athlete.js';
import type { GameScene } from '../game/scene.js';
import type { Hud } from '../screens/hud.js';

/** Everything an event controller can touch. */
export interface EventEnv {
  scene: GameScene;
  synth: Synth;
  hud: Hud;
  athlete: Athlete;
}

export interface CadenceStats {
  avgCadence: number;
  peakCadence: number;
  presses: number;
  rejected: number;
  suspicious: boolean;
}

export interface EventOutcome {
  mark: number | null;
  stats: CadenceStats;
  /** Extra rows for the result table. */
  details: { label: string; value: string }[];
  /** Coaching lines specific to how the event went. */
  tips: string[];
}

export interface EventController {
  readonly ev: EventDef;
  /** Set up the athlete and HUD; called once after construction. */
  begin(): void;
  control(e: ControlEvent): void;
  frame(dt: number): void;
  /** Set once the event is over; the app shows the result screen. */
  readonly outcome: EventOutcome | null;
  dispose(): void;
}

/** Accumulates cadence statistics across one or more runner states. */
export class CadenceTracker {
  private sum = 0;
  private samples = 0;
  peak = 0;
  presses = 0;
  rejected = 0;
  private intervals: number[] = [];

  /** Call every frame while the athlete is running. */
  sample(cadence: number, dt: number): void {
    this.sum += cadence * dt;
    this.samples += dt;
    this.peak = Math.max(this.peak, cadence);
  }

  /** Fold in a finished run-up or wind-up. */
  absorb(r: { cadence: Cadence }): void {
    this.presses += r.cadence.count;
    this.rejected += r.cadence.rejected;
    this.intervals.push(...r.cadence.intervals);
  }

  stats(): CadenceStats {
    return {
      avgCadence: this.samples > 0 ? this.sum / this.samples : 0,
      peakCadence: this.peak,
      presses: this.presses,
      rejected: this.rejected,
      suspicious: regularity(this.intervals).suspicious,
    };
  }
}

export function cadenceTips(s: CadenceStats, sweet: number): string[] {
  const tips: string[] = [];
  if (s.presses > 10 && s.rejected > s.presses * 0.15)
    tips.push(
      `${s.rejected} presses did not count because they repeated the same key. Alternate strictly: left, right, left, right.`,
    );
  if (s.avgCadence < sweet * 0.7)
    tips.push(
      `Aim for ${sweet} alternating presses a second. Use two fingers on one hand, or one finger per hand.`,
    );
  else if (s.avgCadence < sweet * 0.95)
    tips.push(
      `Good rhythm. The sweet spot is ${sweet} presses a second; a touch faster and you gain real speed.`,
    );
  else
    tips.push(
      `You are in the sweet spot. Above ${sweet} presses a second the gains shrink, so keep it smooth rather than frantic.`,
    );
  if (s.suspicious)
    tips.push(
      'Your key intervals were metronomic. This is flagged in your cadence history; try a comfortable natural rhythm.',
    );
  return tips;
}
